import {
  useState,
  useCallback,
  useEffect,
  createContext,
  FC,
  PropsWithChildren,
  useContext,
} from "react";
import TwilioVideo, {
  createLocalVideoTrack,
  ConnectOptions,
  VideoBandwidthProfileOptions,
  Track,
} from "twilio-video";
import { TrackIdentifier } from "./types";
import useRoom from "./useRoom";
import useLocalTracks from "./useLocalTracks";

export * from "./TwilioVideoLocalView";
export * from "./TwilioVideoParticipantView";

interface Settings {
  trackSwitchOffMode: VideoBandwidthProfileOptions["trackSwitchOffMode"];
  dominantSpeakerPriority?: Track.Priority;
  bandwidthProfileMode: VideoBandwidthProfileOptions["mode"];
  maxAudioBitrate: string;
  contentPreferencesMode?: "auto" | "manual";
  clientTrackSwitchOffControl?: "auto" | "manual";
}

const settings: Settings = {
  trackSwitchOffMode: "disabled",
  dominantSpeakerPriority: "standard",
  bandwidthProfileMode: "collaboration",
  maxAudioBitrate: "16000",
  contentPreferencesMode: "auto",
  clientTrackSwitchOffControl: "auto",
};

const options: ConnectOptions = {
  // Bandwidth Profile, Dominant Speaker, and Network Quality
  // features are only available in Small Group or Group Rooms.
  // Please set "Room Type" to "Group" or "Small Group" in your
  // Twilio Console: https://www.twilio.com/console/video/configure
  bandwidthProfile: {
    video: {
      mode: settings.bandwidthProfileMode,
      dominantSpeakerPriority: settings.dominantSpeakerPriority,
      trackSwitchOffMode: settings.trackSwitchOffMode,
      contentPreferencesMode: settings.contentPreferencesMode,
      clientTrackSwitchOffControl: settings.clientTrackSwitchOffControl,
      maxSubscriptionBitrate: 2500000,
    },
  },
  dominantSpeaker: true,
  networkQuality: { local: 1, remote: 1 },

  // Comment this line if you are playing music.
  maxAudioBitrate: Number(settings.maxAudioBitrate),

  preferredVideoCodecs: "auto",
  audio: true,
  video: false,
  insights: false,
};

type TwilioVideoType = {
  room?: TwilioVideo.Room;
  connect: (
    name: string,
    token: string,
    localTracks: TwilioVideo.LocalTrack[]
  ) => Promise<void>;
  // getAudioAndVideoTracks: () => Promise<void>;
  // setRoom?: (room: TwilioVideo.Room) => void;
  localVideoTrack?: TwilioVideo.LocalVideoTrack;
  setLocalVideoTrack?: (track?: TwilioVideo.LocalVideoTrack) => void;
  remoteVideoTracks: Map<string, TwilioVideo.RemoteVideoTrack>;
  addRemoteVideoTrack?: (track: TwilioVideo.RemoteVideoTrack) => void;
  removeRemoteVideoTrack?: (track: TwilioVideo.RemoteVideoTrack) => void;
};

export const TwilioVideoContext = createContext<TwilioVideoType>(null!);

export const TwilioVideoProvider: FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const onErrorCallback: ErrorCallback = useCallback(
    (error) => {
      console.log(`ERROR: ${error.message}`, error);
      // onError(error);
    },
    []
    // [onError]
  );

  // const {
  //   localTracks,
  //   getLocalVideoTrack,
  //   isAcquiringLocalTracks,
  //   removeLocalAudioTrack,
  //   removeLocalVideoTrack,
  //   getAudioAndVideoTracks,
  // } = useLocalTracks();
  const { room, isConnecting, connect } = useRoom(onErrorCallback, options);

  // const [room, setRoom] = useState<TwilioVideo.Room | undefined>();
  const [localVideoTrack, setLocalVideoTrack] = useState<
    TwilioVideo.LocalVideoTrack | undefined
  >();
  const [remoteVideoTracks, setRemoteVideoTracks] = useState<
    Map<string, TwilioVideo.RemoteVideoTrack>
  >(new Map());
  const addRemoteVideoTrack = useCallback(
    (track: TwilioVideo.RemoteVideoTrack) => {
      setRemoteVideoTracks((v) => new Map([...v, [track.sid, track]]));
    },
    []
  );
  const removeRemoteVideoTrack = useCallback(
    (track: TwilioVideo.RemoteVideoTrack) => {
      const newVideoTracks = new Map(remoteVideoTracks);
      newVideoTracks.delete(track.sid);

      setRemoteVideoTracks(newVideoTracks);
    },
    [remoteVideoTracks]
  );
  return (
    <TwilioVideoContext.Provider
      value={{
        room,
        connect,
        // setRoom,
        localVideoTrack,
        setLocalVideoTrack,
        remoteVideoTracks,
        addRemoteVideoTrack,
        removeRemoteVideoTrack,
      }}
    >
      {children}
    </TwilioVideoContext.Provider>
  );
};

// export const useTwilioVideo = () => {
//   const {
//     room,

//     // setRoom,
//     localVideoTrack,
//     setLocalVideoTrack,
//     remoteVideoTracks,
//     addRemoteVideoTrack,
//     removeRemoteVideoTrack,
//   } = useContext(TwilioVideoContext);

//   const connect = useCallback(
//     (options: {
//       roomName?: string;
//       accessToken: string;
//       // cameraType?: cameraType;
//       // dominantSpeakerEnabled?: boolean;
//       enableAudio?: boolean;
//       enableVideo?: boolean;
//       // encodingParameters?: {
//       //   enableH264Codec?: boolean;
//       //   // if audioBitrate OR videoBitrate is provided, you must provide both
//       //   audioBitrate?: number;
//       //   videoBitrate?: number;
//       // };
//       // enableNetworkQualityReporting?: boolean;
//     }) => {
//       console.log("connect...");
//       TwilioVideo.connect(options.accessToken, {
//         name: options.roomName,
//         video: options.enableVideo,
//         audio: options.enableAudio,
//       })
//         .then((newRoom) => {
//           console.log("newRoom", newRoom);
//           setRoom?.(newRoom);
//           //   setTimeout(() => setVideoRoomId(room.sid), 1000); // TODO: Rebuild without timeout
//           //   console.log('Connected to Room "%s"', room.name);
//           newRoom.on("disconnected", (r, e) => {
//             console.log("room.disconnected", r, e);
//           });
//           newRoom.on("participantConnected", (participant) => {
//             console.log("event.participant.connected.sid", participant.sid);
//           });
//           newRoom.on("participantDisconnected", (participant) => {
//             console.log("event.participant.disconnected.sid", participant.sid);
//           });
//           // newRoom.on("participantDisconnected", (participant) => {
//           //   console.log("event.participant.disconnected.sid", participant.sid);
//           // });
//           // newRoom.on("trackEnabled", (publication, participant) => {
//           //   console.log(
//           //     "event.participant.trackEnabled.sid/kind",
//           //     participant.sid,
//           //     publication.kind
//           //   );
//           // });
//           // newRoom.on("trackDisabled", (publication, participant) => {
//           //   console.log(
//           //     "event.participant.trackDisabled.sid/kind",
//           //     participant.sid,
//           //     publication.kind
//           //   );
//           // });
//           // newRoom.on("trackPublished", (publication, participant) => {
//           //   console.log(
//           //     "event.participant.trackPublished.sid/kind",
//           //     participant.sid,
//           //     publication.kind
//           //   );
//           // });
//           // newRoom.on("trackUnpublished", (publication, participant) => {
//           //   console.log(
//           //     "event.participant.trackUnpublished.sid/kind",
//           //     participant.sid,
//           //     publication.kind
//           //   );
//           // });
//           newRoom.on("trackSubscribed", (track, publication, participant) => {
//             if (track.kind === "video") {
//               addRemoteVideoTrack?.(track);
//             } else {
//             }
//           });
//           newRoom.on("trackUnsubscribed", (track, publication, participant) => {
//             // console.log(
//             //   "event.participant.trackUnsubscribed.sid/kind",
//             //   participant.sid,
//             //   publication.kind
//             // );
//             if (track.kind === "video") {
//               removeRemoteVideoTrack?.(track);
//             } else {
//             }
//           });

//           // newRoom.on("trackSubscriptionFailed", () => {
//           //   console.log("trackSubscriptionFailed...");
//           // });
//           // newRoom.on("trackWarning", () => {
//           //   console.log("trackWarning...");
//           // });
//           // newRoom.on("trackWarningsCleared", () => {
//           //   console.log("trackWarningsCleared...");
//           // });
//           // newRoom.on("participantReconnected", () => {
//           //   console.log("participantReconnected...");
//           // });
//           // newRoom.on("participantReconnecting", () => {
//           //   console.log("participantReconnecting...");
//           // });

//           //   on(event: 'dominantSpeakerChanged', listener: (dominantSpeaker: RemoteParticipant) => void): this;

//           //   on(event: 'participantReconnected', listener: (participant: RemoteParticipant) => void): this;
//           //   on(event: 'participantReconnecting', listener: (participant: RemoteParticipant) => void): this;
//           //   on(event: 'reconnected', listener: () => void): this;
//           //   on(event: 'reconnecting', listener: (error: TwilioError) => void): this;
//           //   on(event: 'recordingStarted', listener: () => void): this;
//           //   on(event: 'recordingStopped', listener: () => void): this;
//           //   on(event: 'trackDimensionsChanged', listener: (track: RemoteVideoTrack, participant: RemoteParticipant) => void): this;

//           //   on(event: 'trackMessage', listener: (data: string | ArrayBuffer, track: RemoteDataTrack, participant: RemoteParticipant) => void): this;

//           //   on(event: 'trackPublishPriorityChanged', listener: (priority: Track.Priority, publication: RemoteTrackPublication, participant: RemoteParticipant) => void): this;
//           //   on(event: 'trackStarted', listener: (track: RemoteTrack, participant: RemoteParticipant) => void): this;
//           //   on(event: 'trackSubscribed', listener: (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => void): this;
//           //   on(event: 'trackSubscriptionFailed', listener: (error: TwilioError, publication: RemoteTrackPublication, participant: RemoteParticipant) => void): this;
//           //   on(event: 'trackSwitchedOff', listener: (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => void): this;
//           //   on(event: 'trackSwitchedOn', listener: (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => void): this;

//           //   on(event: 'trackUnsubscribed', listener: (track: RemoteTrack, publication: RemoteTrackPublication, participant: RemoteParticipant) => void): this;
//           //   on(event: 'trackWarning', listener: (name: string, publication: LocalTrackPublication, participant: LocalParticipant) => void): this;
//           //   on(event: 'trackWarningsCleared', listener: (publication: LocalTrackPublication, participant: LocalParticipant) => void): this;

//           // newRoom.participants.forEach((participant) => {
//           //   console.log("loop.participant.sid", participant.sid);
//           // });
//           //   room.localParticipant.
//           //   // room.localParticipant.videoTracks.forEach((videoTrack) => {
//           //   //   videoTrack.track.disable();
//           //   // });
//         })
//         .catch((e) => {
//           console.error(e);
//         });
//     },
//     [addRemoteVideoTrack, removeRemoteVideoTrack, setRoom]
//   ); // TODO
//   const disconnect = useCallback(() => {
//     if (room) console.log("disconnect...");

//     room?.localParticipant.audioTracks.forEach((publication) => {
//       publication.track.stop();
//       publication.unpublish();
//     });

//     room?.localParticipant.videoTracks.forEach((publication) => {
//       publication.track.stop();
//       publication.unpublish();
//     });

//     room?.disconnect();
//   }, [room]);

//   const setLocalVideoEnabled = useCallback(
//     (enabled: boolean) => {
//       if (localVideoTrack) {
//         localVideoTrack.enable(enabled);
//       }
//     },
//     [localVideoTrack]
//   ); // TODO
//   const setLocalAudioEnabled = useCallback((enabled: boolean) => {
//     // room?.localParticipant.audioTracks.forEach((audioTrack) =>
//     //   audioTrack.track.enable(enabled)
//     // );
//     console.log("setLocalAudioEnabled...add logic");
//   }, []); // TODO
//   const setRemoteAudioEnabled = useCallback((enabled: boolean) => {
//     console.log("setRemoteAudioEnabled...add logic");
//   }, []); // TODO

//   const setVideoDevice = useCallback((device: any) => {
//     console.log("setVideoDevice...add logic");
//   }, []); // TODO
//   const setAudioDevice = useCallback((device: any) => {
//     console.log("setAudioDevice...add logic");
//   }, []); // TODO

//   const publishLocalAudio = useCallback(() => {
//     console.log("publishLocalAudio...add logic");
//   }, []); // TODO
//   const unpublishLocalAudio = useCallback(() => {
//     console.log("unpublishLocalAudio...add logic");
//   }, []); // TODO
//   const publishLocalVideo = useCallback(() => {
//     if (localVideoTrack) {
//       localVideoTrack.restart();
//       room?.localParticipant.publishTrack(localVideoTrack);
//     } else {
//       createLocalVideoTrack()
//         .then((localVideoTrack) => {
//           if (!localVideoTrack.isStarted) {
//             localVideoTrack.restart();
//           }
//           return room?.localParticipant.publishTrack(localVideoTrack);
//         })
//         .then((publication) => {
//           console.log("publication", publication);
//           if (publication) {
//             setLocalVideoTrack?.(
//               publication?.track as TwilioVideo.LocalVideoTrack
//             );
//           } else {
//             alert(
//               "ビデオデバイスが他のアプリで使われているが可能性があるため使えません。"
//             );
//           }
//         })
//         .catch((e) => {
//           console.error(e);
//         });
//     }
//   }, [room, setLocalVideoTrack, localVideoTrack]);
//   const unpublishLocalVideo = useCallback(() => {
//     room?.localParticipant.videoTracks.forEach((publication) => {
//       publication.track.stop();
//       publication.unpublish();
//     });
//     setLocalVideoTrack?.(undefined);
//   }, [room, setLocalVideoTrack]);

//   const flipCamera = useCallback(() => {
//     console.log("flipCamera...add logic");
//   }, []); // TODO

//   return {
//     room,
//     localVideoTrack,
//     remoteVideoTracks,
//     connect,
//     disconnect,
//     setLocalVideoEnabled,
//     setLocalAudioEnabled,
//     setRemoteAudioEnabled,
//     setVideoDevice,
//     setAudioDevice,
//     publishLocalAudio,
//     unpublishLocalAudio,
//     publishLocalVideo,
//     unpublishLocalVideo,
//     flipCamera,
//   } as const;
// };
